import React from 'react'
// libs
import styled, { css } from 'styled-components'
// components
import AvatarsList from 'components/PageComponents/Plans/Edit/components/ObjectivesListPreview/components/PlanDetails/components/AvatarsList'
// context
import { NodeContentBLContext } from 'containers/ObjectivesList/components/NodeContent/layers/BL'
// icons
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LinkIconGray from 'src/icons/link-gray.svg'
import LinkIconWhite from 'src/icons/link-white.svg'
import ArrowIconOrigin from '@mui/icons-material/ArrowForwardIos'
import ReviewIconOrigin from 'src/icons/review-white.svg'
// themes
import colors from 'themes/colors'
import { Chip } from 'themes/views/tables'
// views
import {
  ActionsIcon,
  MeasuresIcon,
  NodeTitleWrapper,
  Content as Wrapper,
  LinkIconWithCounter
} from 'themes/views/objectivesList'
import { AvatarWrapper } from 'components/PageComponents/Home/components/DashboardWidgets/components/StrategyActions/components/Content/components/TasksList/views'
import { ContentInnerWrapper, Path } from '../../ObjectivesList/ObjectivesTreeTheme/NodeContent/views'
// utils
import { size } from 'polished'
import { isEmpty } from 'lodash'
import { truncate } from 'utils/string'
import { isColorLight } from 'utils/tree'

// styled
const AdditionalInfo = styled.div`
  align-items: center;
  display: flex;
  margin-left: auto;
  margin-right: 8px;

  & > div,
  svg {
    margin-left: 8px;
  }

  ${AvatarWrapper} {
    margin-right: 8px;
  }
`

const Content = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  width: calc(100% - 48px);
`

const IconContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  padding: 6px 0;
`

const CollapseIcon = styled(ArrowIconOrigin)<{
  $hidden: boolean
  $isExpanded: boolean
}>`
  margin: 0 5px 0 4px;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};

  path {
    fill: ${colors.blue500};
  }

  ${({ $isExpanded }) =>
    $isExpanded &&
    css`
      transform: rotate(90deg);
    `}
`

const ReviewIcon = styled(ReviewIconOrigin)`
  ${size('20px')};
`

const generateTruncatedLettersCount = ({ isAction, isKPI, isObjective, linkingItems, nodeEvents, node }) => {
  const level = node.level.position

  let num = isObjective ? 110 : isAction || isKPI ? 108 : 0

  num -= level * 3

  if (!isEmpty(linkingItems)) {
    num -= 4
  }

  const assignedPersonsCount = nodeEvents?.assignedPersons?.length || 0

  num -= assignedPersonsCount === 1 ? 4 : assignedPersonsCount > 4 ? 12 : assignedPersonsCount * 3
  num -= (nodeEvents?.action ? 10 : 0) + (nodeEvents?.review ? 10 : 0) + (nodeEvents?.period ? 10 : 0)

  return num
}

const NodeContent = (props) => {
  const { background, hasChildren, isAction, isKPI, isObjective, isRoot, node, nodeTitle } = props

  const { nodeEvents } = React.useContext(NodeContentBLContext)

  const isNodeReferenced = node.referenced
  const linkingItems = [...node.linkingItems.destinationLinkingItems, ...node.linkingItems.sourceLinkingItems]

  return (
    <Wrapper>
      {(isAction || isKPI) && (
        <IconContainer>
          {isAction ? <ActionsIcon /> : <MeasuresIcon />}
          <CollapseIcon color={'inherit'} $hidden={!hasChildren} $isExpanded={node.expanded} />
        </IconContainer>
      )}

      <Content>
        <ContentInnerWrapper $isNodeReferenced={isNodeReferenced}>
          <NodeTitleWrapper $isObjective={isObjective && !isNodeReferenced} $isNodeReferenced={isNodeReferenced}>
            {truncate(nodeTitle, generateTruncatedLettersCount({ ...props, linkingItems, nodeEvents }))}
          </NodeTitleWrapper>
          {isNodeReferenced && <Path>{node.path}</Path>}
        </ContentInnerWrapper>
        <AdditionalInfo>
          {!isEmpty(linkingItems) && (
            <LinkIconWithCounter $background={background} $isRootObjective={isRoot && isObjective}>
              <div>{linkingItems.length}</div>
              {isRoot && isObjective && !isColorLight(background) ? <LinkIconWhite /> : <LinkIconGray />}
            </LinkIconWithCounter>
          )}

          {nodeEvents?.period && (
            <Chip
              rounded
              icon={<AccessTimeIcon />}
              label={nodeEvents.period.chipLabel}
              chipColor={nodeEvents.period.chipColor}
            />
          )}
          {nodeEvents?.action && (
            <Chip
              rounded
              icon={<AccessTimeIcon />}
              label={nodeEvents.action.chipLabel}
              chipColor={nodeEvents.action.chipColor}
            />
          )}
          {nodeEvents?.review && (
            <Chip
              rounded
              icon={<ReviewIcon />}
              label={nodeEvents.review.chipLabel}
              chipColor={nodeEvents.review.chipColor}
            />
          )}

          {nodeEvents.assignedPersons.length >= 1 && <AvatarsList list={nodeEvents.assignedPersons} limit={3} />}
        </AdditionalInfo>
      </Content>
    </Wrapper>
  )
}

export default NodeContent
